<template>
  <div v-if="isLoaded">
    <div>
      <!-- Ist-Zustand bei Aufnahme -->
      <p style="font-size: 16px; font-weight: bold; margin-bottom: 5px;">Ist-Zustand bei Aufnahme</p>
      <div v-if="actualConditions.length > 0">
        <div
            v-for="(actualCondition, index) in actualConditions"
            :key="'condition-' + index"
            class="row mb-2"
            style="font-size: 13px;">
          <div class="col-lg-4 col-md-6 col-sm-12">
            {{ actualCondition.title }}
          </div>
          <div class="col-lg-8 col-md-6 col-sm-12">
            {{ truncate(actualCondition.description) }}
          </div>
        </div>
      </div>
      <div v-else>Keine Angaben</div>

      <!-- Ist-Zustand nach 2.VO -->
      <p style="font-size: 16px; font-weight: bold; margin-bottom: 5px; margin-top: 15px;">
        Ist-Zustand nach 2.VO
      </p>
      <div v-if="actualStates.length > 0">
        <div
            v-for="(actualState, index) in actualStates"
            :key="'state-' + index"
            class="row mb-2"
            style="font-size: 13px;">
          <div class="col-lg-4 col-md-6 col-sm-12">
            {{ actualState.title }}
          </div>
          <div class="col-lg-8 col-md-6 col-sm-12">
            {{ truncate(actualState.description) }}
          </div>
        </div>
      </div>
      <div v-else>Keine Angaben</div>
    </div>

    <form @submit.prevent="submitForm" novalidate class="mt-4">
      <div class="row mt-3">
        <div class="col-8">
          <BaseInput v-model="formData.topic"
                     label="Grund / Thema"/>
        </div>
        <div class="col-lg-12">
          <TextEditor
              v-model="formData.focusOfCare"
              @update:model-value="formData.focusOfCare = $event"
              :inputStyle="{ height: '200px' }"
              label="Betreuungsschwerpunkt"/>
        </div>
        <div class="col-lg-12 mt-4">
          <TextEditor
              v-model="formData.currentSituation"
              @update:model-value="formData.currentSituation = $event"
              :inputStyle="{ height: '200px' }"
              label="Aktuelle Situation"/>
        </div>
      </div>

      <div class="d-flex justify-content-center w-100 mt-4">
        <SaveButton
            :label="$t('general.button.save')"
            :loading="isSubmitting"/>
      </div>
    </form>
  </div>
</template>

<script setup>
import portalSocioTherapyCoachingService from '@/api/Portal/SocioTherapyCoaching';
import BaseInput from '@/components/base/BaseInput.vue';
import TextEditor from '@/components/TextEditor.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import { defineEmits, defineProps, onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import SaveButton from '@/components/button/SaveButton.vue';

const { t }        = useI18n();
const isLoaded     = ref(false);
const isSubmitting = ref(false);
const emit         = defineEmits([ 'update:modelValue', 'update:submitForm' ]);

const props = defineProps({
  coachingId: {
    type: Number,
    required: true,
  },
  interimReportId: {
    type: Number,
    required: true,
  },
  actualConditions: {
    type: Array,
    required: true,
  },
  actualStates: {
    type: Array,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const coachingId       = toRefs(props)[ 'coachingId' ];
const actualConditions = toRefs(props)[ 'actualConditions' ];
const actualStates     = toRefs(props)[ 'actualStates' ];
const interimReportId  = toRefs(props)[ 'interimReportId' ];

const formData = ref({
  coachingId: '',
  topic: '',
  focusOfCare: '',
  currentSituation: '',
});

async function submitForm() {
  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    formData.value.coachingId = +coachingId.value;

    if (props.interimReportId) {
      await portalSocioTherapyCoachingService.putInterimReport(formData.value, +interimReportId.value);
    } else {
      await portalSocioTherapyCoachingService.postInterimReport(formData.value);
    }

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getInterimReportData(interimReportId) {
  try {
    CloseToast();

    const { data } = await portalSocioTherapyCoachingService.getInterimReport(+interimReportId);
    formData.value = {
      ...data.data,
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

const truncate = (text, length) => {
  if (!text || typeof text !== 'string') {
    return ''; // Return an empty string if text is null, undefined, or not a string
  }

  const strippedText = text.replace(/<\/?[^>]+(>|$)/g, ''); // Remove HTML tags
  return strippedText.length > length ? strippedText.substring(0, length) + '...' : strippedText;
};

onMounted(async () => {
  if (interimReportId.value) {
    await getInterimReportData(+interimReportId.value);
  }

  isLoaded.value = true;
});
</script>
<style scoped>

</style>